import React from 'react';
import { X, Mail, Calendar, Cloud, FileText, ChevronRight } from 'lucide-react';
import './AddIntegrationModal.css';

export type IntegrationTypeOption = 'email' | 'calendar' | 'cloud' | 'document';

export interface IntegrationTypeItem {
  type: IntegrationTypeOption;
  name: string;
  description: string;
  comingSoon?: boolean;
}

export interface AddIntegrationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler when an integration type is selected */
  onSelectType: (type: IntegrationTypeOption) => void;
  /** Custom integration types (overrides defaults) */
  integrationTypes?: IntegrationTypeItem[];
  /** Optional custom className */
  className?: string;
  /** Labels for i18n support */
  labels?: AddIntegrationModalLabels;
}

export interface AddIntegrationModalLabels {
  title?: string;
  cancel?: string;
  comingSoon?: string;
}

const defaultLabels: Required<AddIntegrationModalLabels> = {
  title: 'Add Email Inbox',
  cancel: 'Cancel',
  comingSoon: 'Coming Soon',
};

const defaultIntegrationTypes: IntegrationTypeItem[] = [
  {
    type: 'email',
    name: 'Email',
    description: 'Connect your email account to import documents and correspondence',
  },
  {
    type: 'calendar',
    name: 'Calendar',
    description: 'Sync your calendar to track important dates and events',
    comingSoon: true,
  },
  {
    type: 'cloud',
    name: 'Cloud Storage',
    description: 'Import documents from cloud storage providers',
    comingSoon: true,
  },
  {
    type: 'document',
    name: 'Document Scanner',
    description: 'Automatically import scanned documents',
    comingSoon: true,
  },
];

const getIntegrationIcon = (type: IntegrationTypeOption) => {
  switch (type) {
    case 'email':
      return <Mail size={20} />;
    case 'calendar':
      return <Calendar size={20} />;
    case 'cloud':
      return <Cloud size={20} />;
    case 'document':
      return <FileText size={20} />;
    default:
      return <Mail size={20} />;
  }
};

/**
 * AddIntegrationModal - Modal for selecting an integration type to add.
 */
export const AddIntegrationModal: React.FC<AddIntegrationModalProps> = ({
  isOpen,
  onClose,
  onSelectType,
  integrationTypes = defaultIntegrationTypes,
  className = '',
  labels = {},
}) => {
  const l = { ...defaultLabels, ...labels };

  if (!isOpen) return null;

  const handleTypeClick = (item: IntegrationTypeItem) => {
    if (!item.comingSoon) {
      onSelectType(item.type);
    }
  };

  return (
    <div className="add-integration-modal-overlay" onClick={onClose}>
      <div
        className={`add-integration-modal ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-integration-modal-title"
      >
        {/* Header */}
        <div className="add-integration-modal__header">
          <h3 id="add-integration-modal-title" className="add-integration-modal__title">
            {l.title}
          </h3>
          <button
            type="button"
            className="add-integration-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="add-integration-modal__body">
          <div className="add-integration-modal__list">
            {integrationTypes.map((item) => (
              <div
                key={item.type}
                className={`add-integration-modal__item ${item.comingSoon ? 'add-integration-modal__item--disabled' : ''}`}
                onClick={() => handleTypeClick(item)}
                role="button"
                tabIndex={item.comingSoon ? -1 : 0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !item.comingSoon) {
                    e.preventDefault();
                    handleTypeClick(item);
                  }
                }}
              >
                <div className="add-integration-modal__item-content">
                  <div className="add-integration-modal__item-icon">
                    {getIntegrationIcon(item.type)}
                  </div>
                  <div className="add-integration-modal__item-info">
                    <h4 className="add-integration-modal__item-name">
                      {item.name}
                      {item.comingSoon && (
                        <span className="add-integration-modal__coming-soon">
                          {l.comingSoon}
                        </span>
                      )}
                    </h4>
                    <p className="add-integration-modal__item-description">
                      {item.description}
                    </p>
                  </div>
                </div>
                {!item.comingSoon && (
                  <ChevronRight size={18} className="add-integration-modal__item-arrow" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="add-integration-modal__footer">
          <button
            type="button"
            className="add-integration-modal__cancel"
            onClick={onClose}
          >
            {l.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddIntegrationModal;
